import {
  DynamicPage,
  Form,
  HtmlActionElementData,
  SectionData,
  SectionType,
} from '@superfunnel/types';
import { useState } from 'react';

export interface UseEditorModalsReturn {
  settingsModalOpen:
    | { open: boolean; dynamicPageId: string; dynamicPage: DynamicPage }
    | undefined;
  setSettingsModalOpen: (
    state:
      | { open: boolean; dynamicPageId: string; dynamicPage: DynamicPage }
      | undefined,
  ) => void;

  addSectionModalOpen: boolean;
  setAddSectionModalOpen: (open: boolean) => void;

  addFavoriteSectionModalOpen: Partial<SectionData> | undefined;
  setAddFavoriteSectionModalOpen: (
    section: Partial<SectionData> | undefined,
  ) => void;

  addSectionToCatalogModalOpen: SectionData | undefined;
  setAddSectionToCatalogModalOpen: (section: SectionData | undefined) => void;

  removeFavoriteSectionModalOpen:
    | { favoriteSectionId: string; loading?: boolean }
    | undefined;
  setRemoveFavoriteSectionModalOpen: (
    state: { favoriteSectionId: string; loading?: boolean } | undefined,
  ) => void;

  removeSectionFromCatalogModalOpen:
    | { sectionTypeConfigId: string; variantId: string; loading?: boolean }
    | undefined;
  setRemoveSectionFromCatalogModalOpen: (
    state:
      | { sectionTypeConfigId: string; variantId: string; loading?: boolean }
      | undefined,
  ) => void;

  sectionItemEditModalOpen: SectionType | undefined;
  setSectionItemEditModalOpen: (sectionType: SectionType | undefined) => void;

  editFormModalOpen: { open: boolean; form: Form; formId: string } | undefined;
  setEditFormModalOpen: (
    state: { open: boolean; form: Form; formId: string } | undefined,
  ) => void;

  editHtmlStepperModalOpen:
    | { open: boolean; quizId: string; formId: string }
    | undefined;
  setEditHtmlStepperModalOpen: (
    state: { open: boolean; quizId: string; formId: string } | undefined,
  ) => void;

  replaceFormModalOpen:
    | { open: boolean; sectionId: string; currentFormId: string }
    | undefined;
  setReplaceFormModalOpen: (
    state:
      | { open: boolean; sectionId: string; currentFormId: string }
      | undefined,
  ) => void;

  selectImageModalOpen: boolean;
  setSelectImageModalOpen: (open: boolean) => void;

  deleteSectionModalOpen: boolean;
  setDeleteSectionModalOpen: (open: boolean) => void;

  addSectionItemModalOpen: boolean;
  setAddSectionItemModalOpen: (open: boolean) => void;

  /** Used for GlobalSection discard confirmation (websites no longer need this) */
  discardChangesConfirmModalOpen: boolean;
  setDiscardChangesConfirmModalOpen: (open: boolean) => void;

  editQuillSearchParamMentionModalOpen: boolean;
  setEditQuillSearchParamMentionModalOpen: (open: boolean) => void;

  beforeCloseEditorWithoutSaveModalOpen: boolean;
  setBeforeCloseEditorWithoutSaveModalOpen: (open: boolean) => void;

  convertGlobalToLocalModalOpen: boolean;
  setConvertGlobalToLocalModalOpen: (open: boolean) => void;

  convertLocalToGlobalModalOpen:
    | { creating?: boolean; name?: string }
    | undefined;
  setConvertLocalToGlobalModalOpen: (
    state: { creating?: boolean; name?: string } | undefined,
  ) => void;

  editGlobalSectionModalOpen:
    | { open: boolean; globalSectionId: string }
    | undefined;
  setEditGlobalSectionModalOpen: (
    state: { open: boolean; globalSectionId: string } | undefined,
  ) => void;

  pendingAgentChangesModalOpen?: {
    open: boolean;
    loading?: 'approving' | 'rejecting';
  };
  setPendingAgentChangesModalOpen: (
    state: { open: boolean; loading?: 'approving' | 'rejecting' } | undefined,
  ) => void;

  editHtmlActionModalOpen: HtmlActionElementData | undefined;
  setEditHtmlActionModalOpen: (data: HtmlActionElementData | undefined) => void;

  editCTAButtonModalOpen: boolean;
  setEditCTAButtonModalOpen: (open: boolean) => void;
}

export type ModalStates = UseEditorModalsReturn;

export const useEditorModals = (): UseEditorModalsReturn => {
  const [settingsModalOpen, setSettingsModalOpen] = useState<{
    open: boolean;
    dynamicPageId: string;
    dynamicPage: DynamicPage;
  }>();
  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);
  const [addFavoriteSectionModalOpen, setAddFavoriteSectionModalOpen] =
    useState<Partial<SectionData> | undefined>();
  const [addSectionToCatalogModalOpen, setAddSectionToCatalogModalOpen] =
    useState<SectionData | undefined>();
  const [removeFavoriteSectionModalOpen, setRemoveFavoriteSectionModalOpen] =
    useState<{ favoriteSectionId: string; loading?: boolean } | undefined>();
  const [
    removeSectionFromCatalogModalOpen,
    setRemoveSectionFromCatalogModalOpen,
  ] = useState<
    | { sectionTypeConfigId: string; variantId: string; loading?: boolean }
    | undefined
  >();
  const [sectionItemEditModalOpen, setSectionItemEditModalOpen] = useState<
    SectionType | undefined
  >();
  const [editFormModalOpen, setEditFormModalOpen] = useState<
    { open: boolean; form: Form; formId: string } | undefined
  >();
  const [editHtmlStepperModalOpen, setEditHtmlStepperModalOpen] = useState<
    { open: boolean; quizId: string; formId: string } | undefined
  >();
  const [replaceFormModalOpen, setReplaceFormModalOpen] = useState<
    { open: boolean; sectionId: string; currentFormId: string } | undefined
  >();
  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);
  const [deleteSectionModalOpen, setDeleteSectionModalOpen] = useState(false);
  const [addSectionItemModalOpen, setAddSectionItemModalOpen] = useState(false);
  const [
    editQuillSearchParamMentionModalOpen,
    setEditQuillSearchParamMentionModalOpen,
  ] = useState(false);
  const [
    beforeCloseEditorWithoutSaveModalOpen,
    setBeforeCloseEditorWithoutSaveModalOpen,
  ] = useState(false);
  const [convertGlobalToLocalModalOpen, setConvertGlobalToLocalModalOpen] =
    useState(false);
  const [convertLocalToGlobalModalOpen, setConvertLocalToGlobalModalOpen] =
    useState<{ creating?: boolean; name?: string } | undefined>();
  const [editGlobalSectionModalOpen, setEditGlobalSectionModalOpen] = useState<
    { open: boolean; globalSectionId: string } | undefined
  >();
  const [pendingAgentChangesModalOpen, setPendingAgentChangesModalOpen] =
    useState<
      { open: boolean; loading?: 'approving' | 'rejecting' } | undefined
    >();
  const [editHtmlActionModalOpen, setEditHtmlActionModalOpen] = useState<
    HtmlActionElementData | undefined
  >();
  const [editCTAButtonModalOpen, setEditCTAButtonModalOpen] = useState(false);
  /** Used for GlobalSection discard confirmation (websites no longer need this) */
  const [discardChangesConfirmModalOpen, setDiscardChangesConfirmModalOpen] =
    useState(false);

  return {
    settingsModalOpen,
    setSettingsModalOpen,
    addSectionModalOpen,
    setAddSectionModalOpen,
    addFavoriteSectionModalOpen,
    setAddFavoriteSectionModalOpen,
    addSectionToCatalogModalOpen,
    setAddSectionToCatalogModalOpen,
    removeFavoriteSectionModalOpen,
    setRemoveFavoriteSectionModalOpen,
    removeSectionFromCatalogModalOpen,
    setRemoveSectionFromCatalogModalOpen,
    sectionItemEditModalOpen,
    setSectionItemEditModalOpen,
    editFormModalOpen,
    setEditFormModalOpen,
    editHtmlStepperModalOpen,
    setEditHtmlStepperModalOpen,
    replaceFormModalOpen,
    setReplaceFormModalOpen,
    selectImageModalOpen,
    setSelectImageModalOpen,
    deleteSectionModalOpen,
    setDeleteSectionModalOpen,
    addSectionItemModalOpen,
    setAddSectionItemModalOpen,
    discardChangesConfirmModalOpen,
    setDiscardChangesConfirmModalOpen,
    editQuillSearchParamMentionModalOpen,
    setEditQuillSearchParamMentionModalOpen,
    beforeCloseEditorWithoutSaveModalOpen,
    setBeforeCloseEditorWithoutSaveModalOpen,
    convertGlobalToLocalModalOpen,
    setConvertGlobalToLocalModalOpen,
    convertLocalToGlobalModalOpen,
    setConvertLocalToGlobalModalOpen,
    editGlobalSectionModalOpen,
    setEditGlobalSectionModalOpen,
    pendingAgentChangesModalOpen,
    setPendingAgentChangesModalOpen,
    editHtmlActionModalOpen,
    setEditHtmlActionModalOpen,
    editCTAButtonModalOpen,
    setEditCTAButtonModalOpen,
  };
};
